// src/utils/purchaseAmount.js
//
// Pure dollar math for the submission purchase flows. No React, no state — the
// page owns the selected amount and derives everything else through here.

// Crowdfunding sales are created with a fixed $5 share size (utils/api.js), so a
// contribution in whole dollars always maps to a whole number of shares.
export const MIN_CONTRIBUTION = 5;
export const CONTRIBUTION_INCREMENT = 5;

// One-tap amounts offered to contributors, smallest first.
export const CONTRIBUTION_PRESETS = [5, 10, 25, 50];

const round2 = (value) => Math.round(value * 100) / 100;

// The fee is grossed up so the seller nets the asked price after processing.
export const calcHandlingFee = (price, transactingInUS = false) => {
  if (!price || price <= 0) return 0;
  const rate = transactingInUS ? 0.971 : 0.956;
  return round2((price + 0.3) / rate - price);
};

export const snapToIncrement = (value, increment = CONTRIBUTION_INCREMENT) =>
  Math.round(value / increment) * increment;

export const clampAmount = (value, min, max) => Math.min(Math.max(value, min), max);

// Snap first, then clamp. `maxAmount` is always a multiple of the increment for
// crowdfunding sales, so clamping second cannot produce an off-increment amount.
export const normalizeContribution = (value, maxAmount) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return Math.min(MIN_CONTRIBUTION, maxAmount);
  }
  return clampAmount(snapToIncrement(numeric), MIN_CONTRIBUTION, maxAmount);
};

// Never offer more than the campaign can accept. When the remainder falls below
// the largest preset and no preset matches it exactly, one chip covers the rest.
export const contributionChips = (maxAmount) => {
  const presets = CONTRIBUTION_PRESETS.filter((amount) => amount <= maxAmount);
  const chips = presets.map((amount) => ({ amount, label: `$${amount}` }));
  const largestPreset = CONTRIBUTION_PRESETS[CONTRIBUTION_PRESETS.length - 1];

  if (
    maxAmount >= MIN_CONTRIBUTION &&
    maxAmount < largestPreset &&
    !presets.includes(maxAmount)
  ) {
    chips.push({ amount: maxAmount, label: `Fund the rest — $${maxAmount}` });
  }

  return chips;
};

// The single place a dollar amount becomes everything the purchase API needs.
// Buying to own is one copy; contributing is `amountUsd / 5` shares.
export const derivePurchase = ({ amountUsd, pricePerShare, isCrowdFunding }) => {
  const handlingFee = calcHandlingFee(amountUsd);
  return {
    handlingFee,
    totalPrice: round2(amountUsd + handlingFee),
    sharesToBuy: isCrowdFunding ? amountUsd / pricePerShare : 1,
  };
};
